import * as yup from 'yup';

import { ContextVariableConfigurationDict } from './actionBuilder/types';
import { ContextVariableTypes } from './enums';
import { UUID_REGEX } from 'src/constants/regex';
import { isEmailValid } from 'src/pages/solve-analytics/macro-controls-view/utils/handleEmailUtil';
import { ContextVariableValue } from 'src/types/workflowBuilderAPITypes';
import isDecimal from 'validator/lib/isDecimal';

export const getIsValidPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber.length) {
    return false;
  }
  const re = /^[0-9]{11,14}$/;
  return re.test(phoneNumber);
};

// Matches a single context variable reference like `{{cv_id}}` or
// `{{cv_id/./subpath}}`. Anchored so values such as `{{}}`, `{{a}}b{{c}}`,
// or trailing/leading text are rejected.
const CV_REFERENCE_REGEX = /^\{\{[^}]+\}\}$/;

export const isCvReference = (value: unknown): boolean => {
  return typeof value === 'string' && CV_REFERENCE_REGEX.test(value);
};

export const getIsNumberValid = (numberAsString: string): boolean => {
  const number = Number(numberAsString);

  return (
    isDecimal(numberAsString) &&
    number >= Number.MIN_SAFE_INTEGER &&
    number <= Number.MAX_SAFE_INTEGER
  );
};

// Taken from https://stackoverflow.com/questions/6478875/regular-expression-matching-e-164-formatted-phone-numbers
export const isPhoneNumberValid = (phone_number: string): boolean => {
  if (!phone_number.length) {
    return false;
  }

  const re = /^[0-9]{11,14}$/;

  if (re.test(phone_number)) {
    return true;
  }

  return false;
};

export function isContextVariableValueValid({
  type,
  value,
}: {
  type: keyof typeof ContextVariableTypes;
  value: ContextVariableValue;
}) {
  if (type === 'EMAIL') {
    const stringValue = String(value);
    return isEmailValid(stringValue) || isCvReference(stringValue);
  }

  if (type === 'PHONE') {
    return isPhoneNumberValid(String(value));
  }

  if (type === 'NUMBER') {
    const stringValue = String(value);
    return (
      getIsNumberValid(stringValue) ||
      (stringValue.startsWith('{{') && stringValue.endsWith('}}'))
    );
  }

  if (type === 'MULTI_SELECT_LIST') {
    return (value as string[]).length > 0;
  }

  return typeof value === 'string' ? value.trim() : true;
}

export const uuidOrEmptySchema = yup
  .string()
  .test(
    'is-valid-uuid',
    'Value must be a valid UUID, or a list of comma-separated UUIDs',
    value => {
      if (!value) {
        return true;
      }
      return value.split(',').every(uuid => UUID_REGEX.test(uuid));
    },
  );

export const validateTextField = (
  value: string,
  config: ContextVariableConfigurationDict,
): string | null => {
  if (config.isAlphanumeric && !/^[a-zA-Z0-9\s]*$/.test(value)) {
    return 'Value must be alphanumeric';
  }

  if (
    config.minLength !== undefined &&
    config.minLength !== null &&
    value.length < config.minLength
  ) {
    return `Value must be at least ${config.minLength} characters`;
  }

  if (
    config.maxLength !== undefined &&
    config.maxLength !== null &&
    value.length > config.maxLength
  ) {
    return `Value must be at most ${config.maxLength} characters`;
  }

  return null;
};
